import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { CircleAlert, CircleCheck, PencilLine } from 'lucide-react'
import { SortableTable } from '../../components/SortableTable'
import PaginationFooter from '../../components/PaginationFooter'
import { SessionExpired } from '../../components/SessionExpired'
import { useAdminAPI } from '../../hooks/Admin/useAdminAPI'
import { useSetCase } from '../../hooks/Cases/useSetCase'
import { toggleToContinueStatus } from '../../redux/user'
import { setInformInfoInEvaluations } from '../../redux/evaluations'
import {
  Container,
  Header,
  Title,
  MainContent,
  InformButton,
  ErrorMessage
} from './styles'
import { DiagnosisBadge } from '../../components/DiagnosisBadge'

const SORT_FIELD_TYPES = {
  diagnosticoIA: 'text',
  dni: 'number',
  paciente: 'text',
  fechaCaptura: 'date',
  nodoCaptura: 'text',
  institucion: 'text'
}

const getSortDiagnosisValue = (value) => {
  if (value === true) return 2
  if (value === false) return 1
  return 0
}

const normalizeSortValue = (key, value, type) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  if (key === 'diagnosticoIA') {
    return getSortDiagnosisValue(value)
  }

  if (type === 'number') {
    const parsedNumber = Number(value)
    return Number.isNaN(parsedNumber) ? null : parsedNumber
  }

  if (type === 'date') {
    const parsedDate = new Date(value).getTime()
    return Number.isNaN(parsedDate) ? null : parsedDate
  }

  return `${value}`.toLowerCase().trim()
}

const compareValues = (firstValue, secondValue) => {
  if (firstValue === secondValue) return 0
  if (firstValue === null) return 1
  if (secondValue === null) return -1

  if (typeof firstValue === 'number' && typeof secondValue === 'number') {
    return firstValue - secondValue
  }

  return `${firstValue}`.localeCompare(`${secondValue}`, 'es', { sensitivity: 'base' })
}

const formatDate = (d) => {
  if (!d) return '-'
  const dateArray = d.split('-')
  const year = dateArray[0]
  const month = dateArray[1]
  const day = dateArray[2].slice(0, 2)
  return `${day}/${month}/${year}`
}

const getPatientFullName = (singleCase) => {
  const firstName = singleCase && singleCase.paciente && singleCase.paciente.nombre ? singleCase.paciente.nombre : ''
  const lastName = singleCase && singleCase.paciente && singleCase.paciente.apellido ? singleCase.paciente.apellido : ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || '-'
}

export function PendingReports () {
  const { user } = useSelector(state => state)
  const dispatch = useDispatch()
  const history = useHistory()
  const { fetchEntities } = useAdminAPI()
  const { setCase } = useSetCase()

  const [studies, setStudies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startingStudyId, setStartingStudyId] = useState(null)
  const [sortConfig, setSortConfig] = useState({
    key: 'fechaCaptura',
    direction: 'asc'
  })
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    if (user.role !== 'ophthalmologist') {
      history.push('/home')
      return
    }

    const loadPendingStudies = async () => {
      try {
        setLoading(true)
        setError('')
        const result = await fetchEntities('estudios/pendientes')
        const pendingStudies = Array.isArray(result) ? result : []
        setStudies(pendingStudies)
      } catch (loadError) {
        setError('No pudimos cargar los estudios pendientes. Intentalo nuevamente.')
      } finally {
        setLoading(false)
      }
    }

    loadPendingStudies()
  }, [fetchEntities, history, user.role])

  const rows = useMemo(() => {
    return studies.map((singleCase) => {
      return {
        id: singleCase.id,
        diagnosticoIA: singleCase.resumen_DX_IA,
        dni: singleCase && singleCase.paciente && singleCase.paciente.dni ? `${singleCase.paciente.dni}` : '-',
        paciente: getPatientFullName(singleCase),
        fechaCaptura: singleCase.fecha || null,
        nodoCaptura: singleCase && singleCase.nodoCaptura && singleCase.nodoCaptura.nombre ? singleCase.nodoCaptura.nombre : '-',
        institucion: singleCase && singleCase.institucion && singleCase.institucion.nombre ? singleCase.institucion.nombre : '-',
        originalCase: singleCase
      }
    })
  }, [studies])

  const sortedRows = useMemo(() => {
    const fieldType = SORT_FIELD_TYPES[sortConfig.key] || 'text'
    const rowsToSort = [...rows]

    rowsToSort.sort((firstRow, secondRow) => {
      const firstValue = normalizeSortValue(sortConfig.key, firstRow[sortConfig.key], fieldType)
      const secondValue = normalizeSortValue(sortConfig.key, secondRow[sortConfig.key], fieldType)
      const comparisonResult = compareValues(firstValue, secondValue)
      return sortConfig.direction === 'asc' ? comparisonResult : -comparisonResult
    })

    return rowsToSort
  }, [rows, sortConfig])

  const listOfPages = useMemo(() => {
    const pageCount = Math.ceil(sortedRows.length / rowsPerPage)
    return Array.from({ length: pageCount }, (_, index) => index + 1)
  }, [rowsPerPage, sortedRows.length])

  const rowsToShow = useMemo(() => {
    const indexOfLastRow = currentPage * rowsPerPage
    const indexOfFirstRow = indexOfLastRow - rowsPerPage
    return sortedRows.slice(indexOfFirstRow, indexOfLastRow)
  }, [currentPage, rowsPerPage, sortedRows])

  useEffect(() => {
    if (listOfPages.length > 0 && currentPage > listOfPages.length) {
      setCurrentPage(listOfPages.length)
    }
  }, [currentPage, listOfPages])

  const handleSortChange = ({ key, direction }) => {
    setSortConfig({ key, direction })
    setCurrentPage(1)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value))
    setCurrentPage(1)
  }

  const handleStartReport = async (studyId) => {
    try {
      setStartingStudyId(studyId)
      setError('')

      const selectedStudy = await fetchEntities(`estudios/pedir/${studyId}`)
      if (!selectedStudy || selectedStudy.mensaje) {
        throw new Error('Estudio no disponible')
      }

      dispatch(setInformInfoInEvaluations(null))
      await setCase(selectedStudy)
      dispatch(toggleToContinueStatus())
      history.push('/new-report')
    } catch (startError) {
      try {
        const currentPending = await fetchEntities('estudios/pendiente')
        if (currentPending && !currentPending.mensaje) {
          dispatch(setInformInfoInEvaluations(null))
          await setCase(currentPending)
          dispatch(toggleToContinueStatus())
          history.push('/new-report')
          return
        }
      } catch (fallbackError) {}

      setError('No se pudo iniciar el informe para ese estudio. Puede que ya no esté disponible.')
    } finally {
      setStartingStudyId(null)
    }
  }

  const renderDiagnosis = (row) => {
    if (row.diagnosticoIA === true) {
      return (
        <DiagnosisBadge
          tone='referible'
          label='Referible'
          icon={<CircleAlert size={22} />}
        />
      )
    }

    if (row.diagnosticoIA === false) {
      return (
        <DiagnosisBadge
          tone='normal'
          label='Normal'
          icon={<CircleCheck size={22} />}
        />
      )
    }

    return <DiagnosisBadge tone='unknown' label='-' />
  }

  const columns = [
    { key: 'diagnosticoIA', label: 'Diagnóstico IA', sortable: true, width: '1fr', render: renderDiagnosis },
    { key: 'dni', label: 'DNI', sortable: true, width: '0.9fr' },
    { key: 'paciente', label: 'Paciente', sortable: true, width: '1.3fr' },
    { key: 'fechaCaptura', label: 'Fecha de Captura', sortable: true, width: '1fr', render: (row) => formatDate(row.fechaCaptura) },
    { key: 'nodoCaptura', label: 'Nodo de Captura', sortable: true, width: '1.2fr' },
    { key: 'institucion', label: 'Institución', sortable: true, width: '1.2fr' },
    {
      key: 'informar',
      label: 'Informar',
      sortable: false,
      align: 'center',
      width: '0.75fr',
      render: (row) => (
        <InformButton
          title='Informar estudio'
          onClick={() => handleStartReport(row.id)}
          disabled={startingStudyId === row.id}
        >
          <PencilLine size={20} />
        </InformButton>
      )
    }
  ]

  const getRowBackground = (row) => {
    if (row.diagnosticoIA === true) {
      return 'rgba(220, 38, 38, 0.07)'
    }
    return undefined
  }

  return (
    <Container>
      <SessionExpired />

      <Header>
        <Title>Estudios pendientes para informar</Title>
      </Header>

      <MainContent>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <SortableTable
          title={`Estudios Pendientes (${rows.length})`}
          subtitle='Hacé clic en los encabezados para ordenar por columna'
          columns={columns}
          rows={rowsToShow}
          rowKey='id'
          sortBy={sortConfig.key}
          sortDirection={sortConfig.direction}
          onSortChange={handleSortChange}
          getRowBackground={getRowBackground}
          emptyMessage={loading ? 'Cargando estudios pendientes...' : 'No hay estudios pendientes para informar'}
        />

        {!loading && (
          <PaginationFooter
            currentPage={currentPage}
            listOfPages={listOfPages}
            handleChangePage={setCurrentPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </MainContent>
    </Container>
  )
}

export default PendingReports
